import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { getSquadError } from "core-integration/src/store/squad/reducers";
import { createEntry } from "core-integration/src/store/squad/thunks";
import { ICreateEntryData } from "core-integration/src/store/squad/types";
import { getTeams } from "core-integration/src/store/teams/reducers";
import React, { useState } from "react";
import { Trans, withTranslation, WithTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Box } from "rebass/styled-components";
import { getSite } from "../../utils/sites";
import { createInteractionData } from "../../utils/tealium";
import Alert from "../Alert";
import Button from "../Button";
import Dialog from "../Dialog";
import {
  CheckboxField,
  FieldWrap,
  InputField,
  SelectField,
} from "../FieldRenderers";
import { getCodeFromError } from "./helpers";

interface IProps {
  handleHide: () => void;
}

type Props = IProps & WithTranslation;

interface IState extends ICreateEntryData {
  favourite_team: number;
}

const CreateEntryDialog: React.FC<Props> = ({ t, i18n, handleHide }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useNavigate();

  const [state, setState] = useState<IState>({
    email: false,
    favourite_team: 0,
    name: "",
    terms_agreed: false,
    language: i18n.language,
  });

  const error = useSelector((state: RootState) => getSquadError(state));
  const nextEvent = useSelector((state: RootState) => getNextEvent(state));
  const teams = useSelector((state: RootState) => getTeams(state));

  const requiredFields: Array<keyof IState> = [
    "favourite_team",
    "name",
    "terms_agreed",
    "language",
  ];

  const apiDataFromState = () => ({
    email: state.email,
    favourite_team: state.favourite_team > 0 ? state.favourite_team : null,
    name: state.name,
    terms_agreed: state.terms_agreed,
    language: i18n.language,
  });

  const handleFaveChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, favourite_team: parseInt(e.currentTarget.value, 10) });

  const handleNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, name: e.currentTarget.value });

  const handleTermsChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({
      ...state,
      terms_agreed: e.currentTarget.checked ? true : false,
    });

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isValid()) {
      createInteractionData({
        contentTitle: "roster selection",
        interactionId: "nba-fantasy-game:enter-roster:button",
        interactionText: t("createEntryDialog.enterSquad", "Enter roster"),
      });
      dispatch(createEntry(apiDataFromState()))
        .then(() => {
          navigate("/my-team");
        })
        .catch((err) => {
          console.error("Failed to create entry:", err);
        });
    }
  };

  const isValid = () => requiredFields.every((e) => Boolean(state[e]));

  const ageLimit = () => {
    const site = getSite();
    interface Dictionary {
      [key: string]: number;
    }
    const ageLimitDefaults: Dictionary = {
      default: 16,
      main_ar: 16,
      main_he: 16,
      main_tr: 16,
      main_en: 18,
      main_fr: 18,
      main_de: 18,
      main_es: 18,
      main_it: 18,
      main_el: 18,
      main_br: 18,
      lequipe: 18,
    };
    const ageLimit =
      site in ageLimitDefaults
        ? ageLimitDefaults[site.toLowerCase()]
        : ageLimitDefaults["default"];
    return ageLimit;
  };
  const age = ageLimit();

  const errorCode = getCodeFromError(error);

  if (error) {
    const handledErrorCodes = ["price_changed"];
    if (handledErrorCodes.indexOf(errorCode) === -1) {
      window.location.reload();
      return null;
    }
  }
  if (!nextEvent) {
    return (
      <Dialog closeDialog={handleHide}>
        <Dialog.Header closeDialog={handleHide}>
          {t("createEntryDialog.over", "Game over")}
        </Dialog.Header>
        <Dialog.Body isPadded={true}>
          <p>
            {t(
              "createEntryDialog.finished",
              "This game has now finished. Thanks for your interest, come back soon to select your roster for next season's game.",
            )}
          </p>
        </Dialog.Body>
      </Dialog>
    );
  }

  return (
    <Dialog closeDialog={handleHide}>
      <Dialog.Header closeDialog={handleHide}>
        {t("createEntryDialog.enterSquadn", "Enter roster")}
      </Dialog.Header>

      <Dialog.Body isPadded={true}>
        <form onSubmit={handleSubmit}>
          <FieldWrap>
            <InputField
              id="entryName"
              name="entryName"
              label={t("createEntryDialog.teamName", "Team Name")}
              hint={t(
                "createEntryDialog.maxCharacters",
                "Maximum 20 characters",
              )}
              onChange={handleNameChange}
              value={state.name}
              maxLength={20}
            />
          </FieldWrap>
          <FieldWrap>
            <SelectField
              id="entryFave"
              name="entryFave"
              label={t("createEntryDialog.favouriteTeamn", "Favorite Team")}
              onChange={handleFaveChange}
              value={state.favourite_team}
            >
              <option value={0} aria-selected={state.favourite_team === 0}>
                {t("createEntryDialog.chooseATeam", "Choose a team")}...
              </option>

              {teams.map((t) => (
                <option
                  value={t.id}
                  key={t.id}
                  aria-selected={state.favourite_team === t.id}
                >
                  {t.name}
                </option>
              ))}

              <option value={-1} aria-selected={state.favourite_team === -1}>
                {t("createEntryDialog.none", "None")}
              </option>
            </SelectField>
          </FieldWrap>
          <FieldWrap>
            <CheckboxField
              id="entryTerms"
              name="entryTerms"
              label={
                <Trans
                  i18nKey="createEntryDialog.termsNN"
                  values={{ age }}
                  defaults={`I’m aged {{age}} or over and agree to the <3>official rules</3>.`}
                >
                  I’m aged {{ age }} or over and agree to the&nbsp;
                  <a href="/help/terms" target="_blank">
                    official rules
                  </a>
                </Trans>
              }
              onChange={handleTermsChange}
              checked={state.terms_agreed}
            />
          </FieldWrap>
          {errorCode && (
            <Box mb={2}>
              <Alert type="error">
                {errorCode === "price_changed" &&
                  t(
                    "createEntryDialog.errornn",
                    "Unable to enter roster as player salaries have changed. Please refresh the page and try again.",
                  )}
              </Alert>
            </Box>
          )}
          <Button type="submit" disabled={!isValid()}>
            {t("createEntryDialog.enterSquad", "Enter roster")}
          </Button>
        </form>
      </Dialog.Body>
    </Dialog>
  );
};

export default withTranslation()(CreateEntryDialog);
